import { apiService } from './api.service'

export enum OutputTypeConfig {
  PDF = 'pdf',
  PDF_A = 'pdfa',
  PDF_A1 = 'pdfa-1',
  PDF_A2 = 'pdfa-2',
  PDF_A3 = 'pdfa-3',
}

export enum ModeConfig {
  SKIP = 'skip',
  REDO = 'redo',
  FORCE = 'force',
  SKIP_NO_ARCHIVE = 'skip_noarchive',
}

export enum ArchiveFileConfig {
  NEVER = 'never',
  WITH_TEXT = 'with_text',
  ALWAYS = 'always',
}

export enum CleanConfig {
  CLEAN = 'clean',
  FINAL = 'clean-final',
  NONE = 'none',
}

export enum ColorConvertConfig {
  UNCHANGED = 'LeaveColorUnchanged',
  RGB = 'RGB',
  INDEPENDENT = 'UseDeviceIndependentColor',
  GRAY = 'Gray',
  CMYK = 'CMYK',
}

export enum ConfigOptionType {
  String = 'string',
  Number = 'number',
  Select = 'select',
  Boolean = 'boolean',
  JSON = 'json',
  File = 'file',
}

export const ConfigCategory = {
  General: 'Opšte podešavanje',
  OCR: 'OCR podešavanje',
  Barcode: 'Podešavanja barkodova',
  SEF: 'SEF podešavanje',
  EOtpremnica: 'eOtpremnica podešavanje',
}

export interface ConfigOption {
  key: string
  title: string
  titleKey?: string
  type: ConfigOptionType
  choices?: Array<{ id: string; name: string }>
  config_key?: string
  category: string
}

export interface DMSConfig {
  id: number
  output_type: OutputTypeConfig
  pages: number
  language: string
  mode: ModeConfig
  skip_archive_file: ArchiveFileConfig
  image_dpi: number
  unpaper_clean: CleanConfig
  deskew: boolean
  rotate_pages: boolean
  rotate_pages_threshold: number
  max_image_pixels: number
  color_conversion_strategy: ColorConvertConfig
  user_args: object
  app_logo: string | null
  app_logo_icon: string | null
  app_title: string
  app_subtitle: string | null
  show_app_logo_icon: boolean
  show_app_logo: boolean
  show_app_title: boolean
  show_app_subtitle: boolean
  barcodes_enabled: boolean
  barcode_enable_tiff_support: boolean
  barcode_string: string
  barcode_retain_split_pages: boolean
  barcode_enable_asn: boolean
  barcode_asn_prefix: string
  barcode_upscale: number
  barcode_dpi: number
  barcode_max_pages: number
  barcode_enable_tag: boolean
  barcode_tag_mapping: object
  external_integrations_enabled: boolean
  sef_enabled: boolean
  sef_url: string | null
  sef_api_key?: string | null
  sef_api_key_set?: boolean
  sef_fetch_interval_seconds: number
  sef_max_document_age_days: number | null
  sef_tag_ids: number[]
  sef_document_type_sales_380: number | null
  sef_document_type_purchase_380: number | null
  sef_document_type_sales_381: number | null
  sef_document_type_purchase_381: number | null
  sef_document_type_sales_383: number | null
  sef_document_type_purchase_383: number | null
  sef_document_type_sales_386: number | null
  sef_document_type_purchase_386: number | null
  eotpremnica_enabled: boolean
  eotpremnica_url: string | null
  eotpremnica_api_key?: string | null
  eotpremnica_api_key_set?: boolean
  eotpremnica_fetch_interval_seconds: number
  eotpremnica_max_document_age_days: number | null
  eotpremnica_tag_ids: number[]
  eotpremnica_document_type_id: number | null
  eotpremnica_document_type_despatch_incoming_external: number | null
  eotpremnica_document_type_despatch_incoming_internal: number | null
  eotpremnica_document_type_despatch_outgoing_external: number | null
  eotpremnica_document_type_despatch_outgoing_internal: number | null
  eotpremnica_document_type_receipt_incoming_external: number | null
  eotpremnica_document_type_receipt_incoming_internal: number | null
  eotpremnica_document_type_receipt_outgoing_external: number | null
  eotpremnica_document_type_receipt_outgoing_internal: number | null
  docusign_enabled: boolean
  docusign_integration_key?: string | null
  docusign_secret_key?: string | null
  docusign_user_id?: string | null
  docusign_account_id?: string | null
  docusign_base_url?: string | null
  docusign_integration_key_set?: boolean
  docusign_secret_key_set?: boolean
  docusign_user_id_set?: boolean
  docusign_account_id_set?: boolean
  docusign_connect_webhook_info?: {
    connect_id?: string
    name?: string
    url?: string
    registered_at?: string
  } | null
  docusign_connect_url_override?: string | null
  allowed_upload_mime_types?: string[] | null
  workflow_notification_delivery?: 'websocket_only' | 'websocket_and_email'
}

function mapToItems(enumObj: Record<string, string>): Array<{ id: string; name: string }> {
  return Object.keys(enumObj).map((key) => {
    return {
      id: enumObj[key],
      name: enumObj[key],
    }
  })
}

export const DMSConfigOptions: ConfigOption[] = [
  {
    key: 'output_type',
    title: 'Tip izlaza',
    type: ConfigOptionType.Select,
    choices: mapToItems(OutputTypeConfig),
    config_key: 'DOCDOT_OCR_OUTPUT_TYPE',
    category: ConfigCategory.OCR,
  },
  {
    key: 'language',
    title: 'Jezik',
    type: ConfigOptionType.String,
    config_key: 'DOCDOT_OCR_LANGUAGE',
    category: ConfigCategory.OCR,
  },
  {
    key: 'pages',
    title: 'Stranice',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_OCR_PAGES',
    category: ConfigCategory.OCR,
  },
  {
    key: 'mode',
    title: 'Režim',
    type: ConfigOptionType.Select,
    choices: mapToItems(ModeConfig),
    config_key: 'DOCDOT_OCR_MODE',
    category: ConfigCategory.OCR,
  },
  {
    key: 'skip_archive_file',
    title: 'Preskoči arhivski fajl',
    type: ConfigOptionType.Select,
    choices: mapToItems(ArchiveFileConfig),
    config_key: 'DOCDOT_OCR_SKIP_ARCHIVE_FILE',
    category: ConfigCategory.OCR,
  },
  {
    key: 'image_dpi',
    title: 'DPI slike',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_OCR_IMAGE_DPI',
    category: ConfigCategory.OCR,
  },
  {
    key: 'unpaper_clean',
    title: 'Čišćenje',
    type: ConfigOptionType.Select,
    choices: mapToItems(CleanConfig),
    config_key: 'DOCDOT_OCR_CLEAN',
    category: ConfigCategory.OCR,
  },
  {
    key: 'deskew',
    title: 'Ispravka nagiba',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_OCR_DESKEW',
    category: ConfigCategory.OCR,
  },
  {
    key: 'rotate_pages',
    title: 'Rotiranje stranica',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_OCR_ROTATE_PAGES',
    category: ConfigCategory.OCR,
  },
  {
    key: 'rotate_pages_threshold',
    title: 'Prag rotiranja stranica',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_OCR_ROTATE_PAGES_THRESHOLD',
    category: ConfigCategory.OCR,
  },
  {
    key: 'max_image_pixels',
    title: 'Maksimalni pikseli slike',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_OCR_MAX_IMAGE_PIXELS',
    category: ConfigCategory.OCR,
  },
  {
    key: 'color_conversion_strategy',
    title: 'Strategija konverzije boja',
    type: ConfigOptionType.Select,
    choices: mapToItems(ColorConvertConfig),
    config_key: 'DOCDOT_OCR_COLOR_CONVERSION_STRATEGY',
    category: ConfigCategory.OCR,
  },
  {
    key: 'user_args',
    title: 'OCR argumenti',
    type: ConfigOptionType.JSON,
    config_key: 'DOCDOT_OCR_USER_ARGS',
    category: ConfigCategory.OCR,
  },
  {
    key: 'app_logo',
    title: 'App logo',
    titleKey: 'config_app_logo',
    type: ConfigOptionType.File,
    config_key: 'DOCDOT_APP_LOGO',
    category: ConfigCategory.General,
  },
  {
    key: 'app_logo_icon',
    title: 'App icon',
    titleKey: 'config_app_logo_icon',
    type: ConfigOptionType.File,
    config_key: 'DOCDOT_APP_LOGO_ICON',
    category: ConfigCategory.General,
  },
  {
    key: 'app_title',
    title: 'App title',
    titleKey: 'config_app_title',
    type: ConfigOptionType.String,
    config_key: 'DOCDOT_APP_TITLE',
    category: ConfigCategory.General,
  },
  {
    key: 'app_subtitle',
    title: 'App subtitle',
    titleKey: 'config_app_subtitle',
    type: ConfigOptionType.String,
    config_key: 'DOCDOT_APP_SUBTITLE',
    category: ConfigCategory.General,
  },
  {
    key: 'show_app_logo_icon',
    title: 'Show',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_SHOW_APP_LOGO_ICON',
    category: ConfigCategory.General,
  },
  {
    key: 'show_app_logo',
    title: 'Show',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_SHOW_APP_LOGO',
    category: ConfigCategory.General,
  },
  {
    key: 'show_app_title',
    title: 'Show',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_SHOW_APP_TITLE',
    category: ConfigCategory.General,
  },
  {
    key: 'show_app_subtitle',
    title: 'Show',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_SHOW_APP_SUBTITLE',
    category: ConfigCategory.General,
  },
  {
    key: 'barcodes_enabled',
    title: 'Omogući barkodove',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_CONSUMER_ENABLE_BARCODES',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_enable_tiff_support',
    title: 'Omogući TIFF podršku',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_CONSUMER_BARCODE_TIFF_SUPPORT',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_string',
    title: 'String barkoda',
    type: ConfigOptionType.String,
    config_key: 'DOCDOT_CONSUMER_BARCODE_STRING',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_retain_split_pages',
    title: 'Zadrži podeljene stranice',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_CONSUMER_BARCODE_RETAIN_SPLIT_PAGES',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_enable_asn',
    title: 'Omogući ASN',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_CONSUMER_ENABLE_ASN_BARCODE',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_asn_prefix',
    title: 'ASN prefiks',
    type: ConfigOptionType.String,
    config_key: 'DOCDOT_CONSUMER_ASN_BARCODE_PREFIX',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_upscale',
    title: 'Uvećanje',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_CONSUMER_BARCODE_UPSCALE',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_dpi',
    title: 'DPI',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_CONSUMER_BARCODE_DPI',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_max_pages',
    title: 'Maksimalne stranice',
    type: ConfigOptionType.Number,
    config_key: 'DOCDOT_CONSUMER_BARCODE_MAX_PAGES',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_enable_tag',
    title: 'Omogući detekciju tagova',
    type: ConfigOptionType.Boolean,
    config_key: 'DOCDOT_CONSUMER_ENABLE_TAG_BARCODE',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'barcode_tag_mapping',
    title: 'Mapiranje tagova',
    type: ConfigOptionType.JSON,
    config_key: 'DOCDOT_CONSUMER_TAG_BARCODE_MAPPING',
    category: ConfigCategory.Barcode,
  },
  {
    key: 'sef_url',
    title: 'SEF URL',
    type: ConfigOptionType.Select,
    choices: [
      { id: 'https://demoefaktura.mfin.gov.rs/', name: 'Demo (https://demoefaktura.mfin.gov.rs/)' },
      { id: 'https://efaktura.mfin.gov.rs/', name: 'Produkcija (https://efaktura.mfin.gov.rs/)' },
    ],
    category: ConfigCategory.SEF,
  },
  {
    key: 'sef_fetch_interval_seconds',
    title: 'Interval za preuzimanje dokumenata (sek)',
    type: ConfigOptionType.Number,
    category: ConfigCategory.SEF,
  },
  {
    key: 'sef_max_document_age_days',
    title: 'Maksimalna starost dokumenata (dani)',
    type: ConfigOptionType.Number,
    category: ConfigCategory.SEF,
  },
]

class ConfigService {
  async getConfig(): Promise<DMSConfig> {
    const response = await apiService.get<DMSConfig[]>('config/')
    return response.data[0]
  }

  async getDocusignReveal(): Promise<{
    docusign_integration_key: string | null
    docusign_secret_key: string | null
    docusign_user_id: string | null
    docusign_account_id: string | null
  }> {
    const response = await apiService.get<{
      docusign_integration_key: string | null
      docusign_secret_key: string | null
      docusign_user_id: string | null
      docusign_account_id: string | null
    }>('config/docusign-reveal/')
    return response.data
  }

  async saveConfig(config: DMSConfig): Promise<DMSConfig> {
    // Don't pass string for app_logo
    const configToSave = { ...config }
    const fileFields: Array<keyof DMSConfig> = ['app_logo', 'app_logo_icon']
    fileFields.forEach((field) => {
      if (typeof configToSave[field] === 'string') {
        delete (configToSave as any)[field]
      }
    })

    const response = await apiService.patch<DMSConfig>(`config/${config.id}/`, configToSave)
    return response.data
  }

  async uploadFile(file: File, configID: number, configKey: string): Promise<DMSConfig> {
    const formData = new FormData()
    formData.append(configKey, file, file.name)

    const response = await apiService.patch<DMSConfig>(`config/${configID}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async testSefConnection(payload: {
    sef_url?: string | null
  }): Promise<{ success: boolean; version?: string; message?: string }> {
    const response = await apiService.post('config/sef-test/', payload)
    return response.data
  }

  async testEotpremnicaConnection(payload: {
    eotpremnica_url?: string | null
  }): Promise<{ success: boolean; companies_count?: number; message?: string }> {
    const response = await apiService.post('config/eotpremnica-test/', payload)
    return response.data
  }

  async registerDocusignConnect(payload?: {
    name?: string | null
    connect_id?: string | null
    connect_url?: string | null
  }): Promise<{
    success: boolean
    error?: string
    consent_url?: string | null
    connect_id?: string
    name?: string
    url?: string
    registered_at?: string
    api_body?: string
  }> {
    const response = await apiService.post('config/docusign-register-connect/', payload ?? {})
    return response.data
  }

  async finishDocusignSetup(payload?: {
    connect_url?: string | null
  }): Promise<{
    success: boolean
    error?: string
    consent_url?: string | null
    redirect_uri?: string | null
    connect_id?: string
    name?: string
    url?: string
    registered_at?: string
    api_body?: string
  }> {
    const response = await apiService.post('config/docusign-finish-setup/', payload ?? {})
    return response.data
  }
}

export const configService = new ConfigService()
